import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle2, AlertCircle, ArrowRight } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ColumnMapping } from "@/lib/columnMapping";
import type { SchemaField } from "@/lib/columnMapping";
import { useTranslation } from "@/hooks/useTranslation";

interface ColumnMappingStepProps {
  csvHeaders: string[];
  autoMappings: ColumnMapping[];
  schemaFields: SchemaField[];
  onMappingsChange: (mappings: ColumnMapping[]) => void;
  onNext: () => void;
  onBack: () => void;
}

export const ColumnMappingStep = ({
  csvHeaders,
  autoMappings,
  schemaFields,
  onMappingsChange,
  onNext,
  onBack,
}: ColumnMappingStepProps) => {
  const { translate: t } = useTranslation();
  const [mappings, setMappings] = useState<ColumnMapping[]>(autoMappings);

  const handleMappingChange = (csvColumn: string, newMapping: string) => {
    const updated = mappings.map(m =>
      m.csvColumn === csvColumn ? { ...m, mappedTo: newMapping === "skip" ? null : newMapping } : m
    );
    setMappings(updated);
    onMappingsChange(updated);
  };

  const requiredFields = schemaFields.filter(f => f.required);
  const mappedFields = new Set(mappings.filter(m => m.mappedTo).map(m => m.mappedTo));
  const missingRequired = requiredFields.filter(f => !mappedFields.has(f.field));
  const canProceed = missingRequired.length === 0;

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return "bg-secondary";
    if (confidence >= 0.5) return "bg-yellow-500";
    return "bg-orange-500";
  };

  const getConfidenceLabel = (confidence: number) => {
    if (confidence >= 0.8) return t("confidence_high");
    if (confidence >= 0.5) return t("confidence_medium");
    return t("confidence_low");
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">{t("col_mapping_title")}</h3>
        <p className="text-sm text-muted-foreground">
          {t("col_mapping_desc")}
        </p>
      </div>

      {missingRequired.length > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {t("col_mapping_missing_required")}: {missingRequired.map(f => f.label).join(", ")}
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-3">
        {mappings.map((mapping, index) => (
          <Card key={index}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <CardTitle className="text-base">
                    {mapping.csvColumn}
                  </CardTitle>
                  <CardDescription className="text-xs">
                    {t("col_mapping_csv_col_from_file")}
                  </CardDescription>
                </div>
                {mapping.confidence > 0 && (
                  <Badge variant="secondary" className={`${getConfidenceColor(mapping.confidence)} text-white`}>
                    {getConfidenceLabel(mapping.confidence)} ({Math.round(mapping.confidence * 100)}%)
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div className="flex-1">
                    <Label htmlFor={`mapping-${index}`} className="text-xs text-muted-foreground mb-1 block">
                      {t("col_mapping_maps_to")}
                    </Label>
                    <Select
                      value={mapping.mappedTo || "skip"}
                      onValueChange={(value) => handleMappingChange(mapping.csvColumn, value)}
                    >
                      <SelectTrigger id={`mapping-${index}`} className="bg-background">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-background z-50">
                        <SelectItem value="skip" className="text-muted-foreground">
                          Skip this column
                        </SelectItem>
                        {schemaFields.map((field) => (
                          <SelectItem key={field.field} value={field.field}>
                            {field.label} {field.required && <span className="text-destructive">*</span>}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {mapping.suggestions.length > 0 && mapping.mappedTo !== mapping.suggestions[0].field && (
                  <div className="text-xs text-muted-foreground pl-7">
                    <span className="font-medium">Suggestions:</span>{" "}
                    {mapping.suggestions.slice(0, 3).map((s, i) => (
                      <span key={i}>
                        {i > 0 && ", "}
                        <button
                          onClick={() => handleMappingChange(mapping.csvColumn, s.field)}
                          className="text-primary hover:underline"
                        >
                          {schemaFields.find(f => f.field === s.field)?.label}
                        </button>
                        {" "}({Math.round(s.confidence * 100)}%)
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex items-center justify-between pt-4 border-t">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <div className="flex items-center gap-2">
          {canProceed ? (
            <>
              <CheckCircle2 className="h-5 w-5 text-secondary" />
              <span className="text-sm text-muted-foreground">{t("col_mapping_all_mapped")}</span>
            </>
          ) : (
            <>
              <AlertCircle className="h-5 w-5 text-destructive" />
              <span className="text-sm text-destructive">{t("col_mapping_map_required")}</span>
            </>
          )}
        </div>
        <Button onClick={onNext} disabled={!canProceed}>
          Preview Data
        </Button>
      </div>
    </div>
  );
};
